/**
 * 走行の共有。
 *
 * ランキングとリプレイは元から拡散素材なので、**結果画面から 1 タップで出せる**
 * ようにする。ここが無いと、面白かった人が人に見せる手段を持たない。
 *
 * **共有リンクは同じ海へ着地させる。** ステージと走り方を URL に載せ、
 * 受け取った人が「その海」をそのまま走れるようにする。
 * とくに今日の海は全員同じ配置なので、比べる相手がいる状態で始められる。
 */

import type { RunMode } from './scenes/MenuScene';

export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'failed';

/** 共有した先が開く URL。いまのページを基準にする（ドメインを直に書かない）。 */
export function shareUrl(stageId: string, mode: RunMode): string {
  try {
    const url = new URL(window.location.href);
    // 余計なものを引き継がせない。落とし先を決めるものだけ載せる。
    url.search = '';
    url.hash = '';
    url.searchParams.set('s', stageId.replace(/-kids$/, ''));
    if (mode !== 'normal') url.searchParams.set('m', mode);
    return url.toString();
  } catch {
    return 'https://escape-the-waves.pages.dev/';
  }
}

/**
 * 共有する。
 *
 * OS の共有シートが使えればそれを出し、無ければクリップボードへ写す。
 * **取り消しを失敗として扱わない。** 共有シートを閉じただけの人に
 * エラーを見せると、こちらの不具合だと思われる。
 */
export async function shareRun(text: string, url: string): Promise<ShareResult> {
  const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
  if (typeof nav.share === 'function') {
    try {
      await nav.share({ text, url });
      return 'shared';
    } catch (error) {
      // AbortError は本人が閉じただけ。それ以外はコピーに落とす。
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    }
  }
  try {
    await navigator.clipboard.writeText(`${text}\n${url}`);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/** 共有リンクから開かれたときの着地先。無ければ null。 */
export function landingFromUrl(): { stageId: string; mode: RunMode } | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const stageId = params.get('s');
    if (!stageId) return null;
    const raw = params.get('m');
    const mode: RunMode = raw === 'daily' || raw === 'kids' ? raw : 'normal';
    return { stageId, mode };
  } catch {
    return null;
  }
}
